import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, TextField } from '@mui/material';

import { ReactComponent as CloseIcon } from 'assets/images/close-icon.svg';
import { ReactComponent as FolderIcon } from 'assets/images/folder-icon-green.svg';
import { Button, Error, Text } from 'ui/atoms';

const FOLDER_NAME_PATTERN = /^[a-zA-Z0-9 ._()-]+$/;

interface Props {
  /** Every folder, as a '/' separated path below the root directory. */
  folderPaths: string[];
  rootLabel: string;
  /** Selected folder path, '' for the root directory. */
  value: string;
  onChange: (path: string) => void;
  /** Fires while a new folder is being named, before it is created and selected. */
  onAddingFolderChange: (isAddingFolder: boolean) => void;
  dataCy?: string;
}

const getParentPath = (path: string) => path.split('/').slice(0, -1).join('/');

const getAncestorPaths = (path: string) => {
  const segments = path ? path.split('/') : [];

  return segments.map((_, i) => segments.slice(0, i).join('/'));
};

/**
 * Picks a folder from a directory tree. Folders can be expanded to show the ones nested inside them,
 * and one new folder can be added to any of them. Creating a folder selects it and locks the
 * selection, since a folder only exists once documents are put in it. New folders only live in this
 * component, the caller decides what to do with the selected path.
 */
export const DirectorySelect: React.FC<Props> = (props) => {
  const [expandedPaths, setExpandedPaths] = useState<string[]>(() => [
    ...getAncestorPaths(props.value),
    props.value
  ]);
  const [createdPath, setCreatedPath] = useState<string>();
  const [newFolderParent, setNewFolderParent] = useState<string>();
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState('');
  const [showNewFolderHint, setShowNewFolderHint] = useState(false);

  const folderPaths = createdPath ? [...props.folderPaths, createdPath] : props.folderPaths;
  const createdFolderName = createdPath?.split('/').pop();

  const getChildPaths = (parentPath: string) =>
    folderPaths.filter((path) => getParentPath(path) === parentPath).sort((a, b) => a.localeCompare(b));

  const expandPath = (path: string) => {
    setExpandedPaths((paths) => (paths.includes(path) ? paths : [...paths, path]));
  };

  const toggleExpanded = (path: string) => {
    setExpandedPaths((paths) => (paths.includes(path) ? paths.filter((p) => p !== path) : [...paths, path]));
  };

  const openNewFolderRow = (parentPath?: string) => {
    setNewFolderParent(parentPath);
    props.onAddingFolderChange(parentPath !== undefined);
  };

  const startNewFolder = (parentPath: string) => {
    openNewFolderRow(parentPath);
    setNewFolderName('');
    setError('');
    expandPath(parentPath);
  };

  const createFolder = () => {
    const name = newFolderName.trim();
    const path = [newFolderParent, name].filter(Boolean).join('/');

    if (!FOLDER_NAME_PATTERN.test(name)) {
      setError('Use only letters, numbers, spaces and . _ - ( )');
      return;
    }

    if (folderPaths.includes(path)) {
      setError('That folder already exists here');
      return;
    }

    setCreatedPath(path);
    openNewFolderRow(undefined);
    props.onChange(path);
  };

  const removeCreatedFolder = () => {
    props.onChange(getParentPath(createdPath ?? ''));
    setCreatedPath(undefined);
    setShowNewFolderHint(false);
  };

  const selectPath = (path: string) => {
    if (createdPath && path !== createdPath) {
      setShowNewFolderHint(true);
      return;
    }

    setShowNewFolderHint(false);
    props.onChange(path);
  };

  const newFolderRow = (depth: number) => {
    return (
      <Box sx={{ pl: depth * 4 + 1, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            autoFocus
            size="small"
            placeholder="Folder name"
            value={newFolderName}
            onChange={(event) => setNewFolderName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                createFolder();
              }

              if (event.key === 'Escape') {
                openNewFolderRow(undefined);
              }
            }}
          />

          <Button color="primary-text" label="Add" onClick={createFolder} />

          <Button color="text" label="Cancel" onClick={() => openNewFolderRow(undefined)} />
        </Box>

        {error ? <Error content={error} /> : null}
      </Box>
    );
  };

  const folderRow = (path: string, label: string, depth: number) => {
    const childPaths = getChildPaths(path);
    const isExpanded = expandedPaths.includes(path);
    const isSelected = props.value === path;
    // A new folder is the only place its documents can go, so it locks the selection.
    const isLocked = Boolean(createdPath) && path !== createdPath;
    const hoverBackgroundColor = isLocked || isSelected ? undefined : 'primary.screen';

    return (
      <Box key={path || 'root'}>
        <Box
          data-cy={`directory-option-${path || 'root'}`}
          onClick={() => selectPath(path)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: '5px',
            pl: depth * 3 + 1,
            pr: 1,
            backgroundColor: isSelected ? 'primary.light' : 'transparent',
            '&:hover': { backgroundColor: hoverBackgroundColor },
            '&:hover .directory-select-add': { visibility: 'visible' }
          }}
        >
          <Box
            sx={{ display: 'flex', visibility: childPaths.length ? 'visible' : 'hidden' }}
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded(path);
            }}
          >
            {isExpanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </Box>

          <FolderIcon height="20px" width="20px" />

          <Text content={label} sx={{ flexGrow: 1, py: 1 }} />

          {path === createdPath ? (
            <Box
              component={CloseIcon}
              data-cy="remove-new-folder"
              height="16px"
              width="16px"
              sx={{ cursor: 'pointer' }}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                removeCreatedFolder();
              }}
            />
          ) : null}

          {createdPath ? null : (
            <Box className="directory-select-add" sx={{ visibility: 'hidden' }}>
              <Button color="text" label="+ New Folder" onClick={() => startNewFolder(path)} />
            </Box>
          )}
        </Box>

        {isExpanded ? (
          <>
            {newFolderParent === path ? newFolderRow(depth + 1) : null}

            {childPaths.map((childPath) => folderRow(childPath, childPath.split('/').pop() ?? '', depth + 1))}
          </>
        ) : null}
      </Box>
    );
  };

  return (
    <Box data-cy={props.dataCy} sx={{ width: '100%' }}>
      <Box
        sx={{
          maxHeight: '300px',
          overflowY: 'auto',
          border: '1px solid #C6CCDA',
          borderRadius: '5px',
          p: 1
        }}
      >
        {folderRow('', props.rootLabel, 0)}
      </Box>

      {showNewFolderHint ? (
        <Error
          dataCy="new-folder-hint"
          content={`Select "${createdFolderName}" or your new folder won't be saved. Remove it with the x to pick another folder.`}
        />
      ) : null}
    </Box>
  );
};
